import { v4 as uuidv4 } from "uuid";

import type {
  ExerciseFormData,
  ExerciseLevelFormData,
  PostExercisePayload,
  PostLevelPayload,
  PostTranslationPayload,
} from "../schemas/exercises";
import type { UploadFileArgs } from "../schemas/files";
import type { DurationIcon, DurationUnit } from "../schemas/shared";

const durationTranslations: Record<DurationUnit, Record<string, string>> = {
  seconds: {
    en: "seconds",
    sv: "sekunder",
  },
  minutes: {
    en: "minutes",
    sv: "minuter",
  },
  repetitions: {
    en: "repetitions",
    sv: "repetitioner",
  },
  repetitions_twice: {
    en: "repetitions x2",
    sv: "repetitioner x2",
  },
  steps: {
    en: "steps",
    sv: "steg",
  },
};

export function getDurationTipText(durationUnit: DurationUnit, locale: string) {
  if (locale in durationTranslations[durationUnit]) {
    return durationTranslations[durationUnit][locale];
  }
  return durationTranslations[durationUnit].en;
}

export function getDurationIcon(durationUnit: DurationUnit): DurationIcon {
  switch (durationUnit) {
    case "seconds":
    case "minutes":
      return "timer";
    case "repetitions":
    case "repetitions_twice":
    case "steps":
      return "repetitions";
    default:
      return "repetitions";
  }
}

export function createExercisePayload(formData: ExerciseFormData): PostExercisePayload {
  return {
    primary_function: formData.primary_function,
    target_joints: formData.target_joints,
    levels: formData.levels.map(level => ({
      duration_unit: level.duration_unit,
      equipment: level.equipment,
      image_url: level.image_url,
      movement: level.movement === "" ? null : level.movement,
      position: level.position === "" ? null : level.position,
      reps: level.reps,
      sets: level.sets,
      translations: level.translations,
      video: level.video,
    })),
  };
}

export function createLevelPayload(level: ExerciseLevelFormData): PostLevelPayload {
  return {
    duration_icon: getDurationIcon(level.duration_unit),
    duration_unit: level.duration_unit,
    equipment: level.equipment,
    level: 1,
    movement: level.movement === "" ? null : level.movement,
    position: level.position === "" ? null : level.position,
    reps: level.reps,
    sets: level.sets,
  };
}

export function createTranslationPayload(level: ExerciseLevelFormData): PostTranslationPayload {
  const {
    image_url,
    video: { video_file_url },
  } = level;
  const video_thumbnail_file_url = video_file_url
    ? video_file_url.replace("videos/video.m3u8", "thumbs/thumb_screen.0000000.jpg")
    : "";

  const translation = level.translations[0];
  const language = translation.locale.split("-")[0];

  return {
    ...translation,
    image_url,
    video_file_url,
    instructions_icon_name: getDurationIcon(level.duration_unit),
    video_thumbnail_file_url,
    instructions_text2: getDurationTipText(level.duration_unit, language),
  };
}

export function generateFileName({ file, language }: Omit<UploadFileArgs, "invalidate">) {
  let fileName = uuidv4();

  switch (file.type) {
    case "video/mp4":
      fileName += ".mp4";
      break;
    case "text/vtt":
      if (!language) {
        throw new Error("language is required for caption files");
      }
      fileName += ".vtt";
      break;
    case "image/svg+xml":
      fileName += ".svg";
      break;
    case "image/gif":
      fileName += ".gif";
      break;
    case "image/jpeg":
      fileName += ".jpg";
      break;
    case "image/png":
      fileName += ".png";
      break;
    default:
      throw new Error(`Unsupported file type: ${file.type}`);
  }

  return fileName;
}
